import { CalendarDate, Time } from '@internationalized/date';
import { Booking, Callsheet, CallsheetRecipient, Production } from '__types__';
import map from 'lodash/map';
import omit from 'lodash/omit';
import moment from 'moment';
import {
  GeneratedCallsheetData,
  CreateCallsheetGroup,
  CreateCallsheetRecipient,
  CreateCallsheetSection,
  CreateCallsheetTeamMember
} from 'v5/api/callsheet/callsheet.api.http.converter';
import {
  generateCallsheetDataFromProductions,
  generateCallsheetDataFromProductionsForRange
} from '../../CallsheetService/_private/generateCallsheetDataFromProductions';
import { duplicateCallsheetLocations } from '../../CallsheetService/_private/duplicateCallsheetLocations';
import { CallsheetDateSelection } from 'v5/features/callsheets/types';
import {
  buildTeamMemberCreateRecipients,
  recipientsForTeamMember
} from 'v5/features/callsheets/callsheetTeamMemberEmails';

/**
 * Fields that should be stripped when duplicating a callsheet
 * These are system-managed fields that should not be copied
 */
const CALLSHEET_FORBIDDEN_DUPLICATE_FIELDS = [
  'archived',
  'created_at',
  'created_mechanism',
  'enable_notifications',
  'guid',
  'legacy_user_id',
  'published_at',
  'updated_at',
  'user_id',
  'id',
  // Don't copy these - they'll be regenerated or set fresh
  'status',
  'published'
] as const;

export interface DuplicateCallsheetInput {
  /** The original callsheet to duplicate */
  originalCallsheet: Callsheet;
  /** Date selection - single date or range */
  dateSelection: CallsheetDateSelection;
  /** Optional title override (defaults to original title) */
  title?: string;
  /** Optional call time override */
  callTime?: Time | null;
}

export interface DuplicateCallsheetResult {
  /** Data ready to be sent to the create API */
  data: GeneratedCallsheetData & {
    title: string;
    from_date: string;
    to_date?: string;
    call_time?: string;
    timezone?: string;
    production_date: string;
    production_id?: number | null;
    linked_productions?: Array<{ id: number }>;
    enterprise_workspace_id: number | null;
    enterprise_team_id: number | null;
    description?: string;
    note?: string;
    billing?: string;
    social?: string;
    hard_drive?: string;
    client_logo?: string;
    schedule?: any[];
    points_of_contact?: any[];
    attachments?: any[];
  };
  /** Metadata for UI display */
  meta: {
    /** Number of team members per production */
    teamMemberCountByProductionId: Record<number, number>;
    /** Whether the callsheet has linked productions */
    hasLinkedProductions: boolean;
    /** Production IDs linked to this callsheet */
    linkedProductionIds: number[];
  };
}

function duplicateExternalRecipients(
  recipients: CallsheetRecipient[] | undefined
): CreateCallsheetRecipient[] {
  return (recipients ?? []).flatMap(recipient => {
    if (recipient.contact_id) {
      return [];
    }

    const emailAddress = recipient.email_address?.trim();
    if (!emailAddress) {
      return [];
    }

    return [
      {
        email_address: emailAddress,
        ...(recipient.alt_name ? { alt_name: recipient.alt_name } : {})
      }
    ];
  });
}

/**
 * Generates duplicate callsheet data from an original callsheet.
 *
 * This function handles two scenarios:
 * 1. Callsheet WITH linked productions: Regenerates groups, sections, and team_members
 *    from the productions' confirmed bookings for the selected date(s)
 * 2. Callsheet WITHOUT linked productions: Copies existing groups, sections, and team_members
 *    with new temporary IDs
 *
 * New features supported:
 * - linked_productions (many-to-many): Copied from original
 * - sections: Either regenerated from productions or copied with new #id references
 * - date ranges: Supports both single date and date range selections
 *
 * @param input - The duplication input parameters
 * @param productions - Array of linked productions (if any)
 * @param bookingsStore - Bookings data for confirmed slot lookup
 */
export function generateDuplicateCallsheetData(
  input: DuplicateCallsheetInput,
  productions: Production[],
  bookingsStore: Record<Booking['id'], Booking>
): DuplicateCallsheetResult {
  const { originalCallsheet, dateSelection, title, callTime } = input;

  // Extract fromDate and toDate from dateSelection
  const fromDate =
    dateSelection.type === 'single' ? dateSelection.date : dateSelection.from;
  const toDate: CalendarDate | undefined =
    dateSelection.type === 'range' ? dateSelection.to : undefined;

  // Sanitize original callsheet by removing system fields
  const sanitizedCallsheet = omit(
    originalCallsheet,
    CALLSHEET_FORBIDDEN_DUPLICATE_FIELDS
  );

  // Extract linked production IDs
  const linkedProductionIds =
    originalCallsheet.linked_productions?.map(lp => lp.id) ??
    (originalCallsheet.production_id ? [originalCallsheet.production_id] : []);

  const hasLinkedProductions = linkedProductionIds.length > 0;
  const externalRecipients = duplicateExternalRecipients(
    sanitizedCallsheet.recipients
  );

  // Handle to_date - use the selected toDate if it's a range, otherwise use original
  let toDateStr: string | undefined;
  if (toDate) {
    toDateStr = toDate.toString();
  } else if (sanitizedCallsheet.to_date) {
    const originalToDate = sanitizedCallsheet.to_date;
    if (typeof originalToDate === 'string') {
      toDateStr = (originalToDate as string).split('T')[0];
    } else {
      toDateStr = moment(originalToDate).format('YYYY-MM-DD');
    }
  }

  // Base callsheet data that's always copied
  const baseData = {
    title: title ?? sanitizedCallsheet.title ?? '',
    from_date: fromDate.toString(),
    to_date: toDateStr,
    call_time: callTime?.toString() ?? sanitizedCallsheet.call_time,
    timezone: sanitizedCallsheet.timezone ?? moment.tz.guess(),
    production_date: moment().toISOString(),
    enterprise_workspace_id: sanitizedCallsheet.enterprise_workspace_id ?? null,
    enterprise_team_id: sanitizedCallsheet.enterprise_team_id ?? null,
    description: sanitizedCallsheet.description,
    note: sanitizedCallsheet.note,
    billing: sanitizedCallsheet.billing,
    social: sanitizedCallsheet.social,
    hard_drive: sanitizedCallsheet.hard_drive,
    client_logo: (sanitizedCallsheet as any).client_logo,
    schedule: sanitizedCallsheet.schedule,
    points_of_contact: sanitizedCallsheet.points_of_contact,
    // Copy attachments without IDs
    attachments: map(sanitizedCallsheet.attachments, attachment =>
      omit(attachment, ['id', 'entity_id', 'file'])
    ),
    recipients: externalRecipients
  };

  // Handle linked_productions
  const linkedProductionsPayload = linkedProductionIds.map(id => ({ id }));

  // WITH LINKED PRODUCTIONS
  // Regenerate groups, sections, and team_members from productions' confirmed bookings
  // Preserve original team member customizations (display fields, notes, custom call times)
  if (hasLinkedProductions && productions.length > 0) {
    // Use range function for date ranges, single date function for single dates
    const generated =
      dateSelection.type === 'range'
        ? generateCallsheetDataFromProductionsForRange(
            productions,
            bookingsStore,
            {
              dateRange: { from: dateSelection.from, to: dateSelection.to },
              callTime: callTime ?? undefined,
              originalTeamMembers: sanitizedCallsheet.team_members ?? [],
              originalRecipients: sanitizedCallsheet.recipients,
              originalLocations: sanitizedCallsheet.locations ?? []
            }
          )
        : generateCallsheetDataFromProductions(productions, bookingsStore, {
            selectedDate: fromDate,
            callTime: callTime ?? undefined,
            originalTeamMembers: sanitizedCallsheet.team_members ?? [],
            originalRecipients: sanitizedCallsheet.recipients,
            originalLocations: sanitizedCallsheet.locations ?? []
          });

    return {
      data: {
        ...baseData,
        production_id: linkedProductionIds[0] ?? null, // Legacy field - first production
        linked_productions: linkedProductionsPayload,
        groups: generated.data.groups,
        sections: generated.data.sections,
        team_members: generated.data.team_members,
        locations: generated.data.locations
      },
      meta: {
        teamMemberCountByProductionId:
          generated.meta.teamMemberCountByProductionId,
        hasLinkedProductions: true,
        linkedProductionIds
      }
    };
  }

  // WITHOUT LINKED PRODUCTIONS
  // Copy existing groups, sections, and team_members with new temporary IDs
  const duplicatedGroups = duplicateGroups(
    (sanitizedCallsheet as any).groups ?? []
  );
  const duplicatedSections = duplicateSections(
    (sanitizedCallsheet as any).sections ?? []
  );
  const duplicatedTeamMembers = duplicateTeamMembers(
    (sanitizedCallsheet as any).team_members ?? [],
    sanitizedCallsheet.recipients
  );
  return {
    data: {
      ...baseData,
      production_id: null,
      linked_productions: [],
      groups: duplicatedGroups,
      sections: duplicatedSections,
      team_members: duplicatedTeamMembers,
      locations: duplicateCallsheetLocations(sanitizedCallsheet.locations ?? [])
    },
    meta: {
      teamMemberCountByProductionId: {},
      hasLinkedProductions: false,
      linkedProductionIds: []
    }
  };
}

/**
 * Duplicates groups with new #id references
 */
function duplicateGroups(groups: any[]): CreateCallsheetGroup[] {
  return map(groups, group => ({
    '#id': `group-${group.id}`,
    name: group.name,
    order: group.order
  }));
}

/**
 * Duplicates sections with new #id references and updated group #refs
 */
function duplicateSections(sections: any[]): CreateCallsheetSection[] {
  return map(sections, section => ({
    '#id': `section-${section.id}`,
    name: section.name,
    order: section.order,
    metadata: section.metadata,
    groups: map(section.groups ?? [], (groupRef: any) => {
      // Handle both existing id references and #ref references
      const groupId = groupRef.id ?? groupRef['#ref']?.replace('group-', '');
      return { '#ref': `group-${groupId}` };
    })
  }));
}

/**
 * Duplicates team members with new temporary IDs and updated group #refs
 */
function duplicateTeamMembers(
  teamMembers: any[],
  flatRecipients: CallsheetRecipient[] | undefined
): CreateCallsheetTeamMember[] {
  return map(teamMembers, teamMember => {
    const cleaned = omit(teamMember, [
      'contact',
      'callsheet_id',
      'id',
      'recipients'
    ]);

    // Handle group reference - convert to #ref format
    let groupRef: { '#ref': string } | undefined;
    if (teamMember.group_id) {
      groupRef = { '#ref': `group-${teamMember.group_id}` };
    } else if (teamMember.group?.['#ref']) {
      groupRef = teamMember.group;
    } else if (teamMember.group?.id) {
      groupRef = { '#ref': `group-${teamMember.group.id}` };
    }

    return {
      ...cleaned,
      group: groupRef,
      recipients: teamMember.contact_id
        ? buildTeamMemberCreateRecipients({
            contactId: Number(teamMember.contact_id),
            recipients: recipientsForTeamMember(flatRecipients, teamMember.id)
          })
        : undefined
    } as CreateCallsheetTeamMember;
  });
}
